"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useLanguage } from "@/contexts/language-context"
import { authAPI } from "@/lib/api"
import { Loader2, AlertCircle, X } from "lucide-react"

export default function AuthPage() {
  const { t } = useLanguage()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingProvider, setLoadingProvider] = useState<"google" | "github" | null>(null)

  // 检查URL参数中是否有OAuth回调
  useEffect(() => {
    const code = searchParams.get("code")
    const provider = searchParams.get("provider")
    const error = searchParams.get("error")

    if (error) {
      setError(t("authError"))
      return
    }

    if (code && provider) {
      handleOAuthCallback(code, provider as "google" | "github")
    }
  }, [searchParams])

  /**
   * 处理OAuth回调
   * @param code - 授权码
   * @param provider - 提供商（google或github）
   */
  const handleOAuthCallback = async (code: string, provider: "google" | "github") => {
    setIsLoading(true)
    setError(null)

    try {
      let result

      if (provider === "google") {
        result = await authAPI.googleLogin(code)
      } else {
        result = await authAPI.githubLogin(code)
      }

      if (result.access_token) {
        // 登录成功，跳转到首页
        router.push("/")
      } else {
        throw new Error(result.message || t("loginFailed"))
      }
    } catch (error) {
      console.error("OAuth登录失败:", error)
      // 在开发模式下，模拟登录通常会成功
      setError(null)
      router.push("/")
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * 处理Google登录 - 简化版本，直接模拟登录
   */
  const handleGoogleLogin = async () => {
    setLoadingProvider("google")
    setIsLoading(true)
    setError(null)

    try {
      // 在开发环境中，直接调用模拟登录
      const result = await authAPI.googleLogin("mock_google_code")

      if (result.access_token) {
        router.push("/")
      }
    } catch (error) {
      console.error("Google登录失败:", error)
      setError(t("loginInitFailed"))
    } finally {
      setIsLoading(false)
      setLoadingProvider(null)
    }
  }

  /**
   * 处理GitHub登录 - 简化版本，直接模拟登录
   */
  const handleGitHubLogin = async () => {
    setLoadingProvider("github")
    setIsLoading(true)
    setError(null)

    try {
      // 在开发环境中，直接调用模拟登录
      const result = await authAPI.githubLogin("mock_github_code")

      if (result.access_token) {
        router.push("/")
      }
    } catch (error) {
      console.error("GitHub登录失败:", error)
      setError(t("loginInitFailed"))
    } finally {
      setIsLoading(false)
      setLoadingProvider(null)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] py-12 bg-[#1a1310] w-full">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0"
            onClick={() => router.push("/")}
            aria-label={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
          <CardTitle className="text-2xl font-bold tracking-tight">{t("welcomeTitle")}</CardTitle>
          <CardDescription>{t("welcomeDesc")}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* 错误提示 */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Google登录按钮 */}
          <Button onClick={handleGoogleLogin} disabled={isLoading} variant="outline" className="w-full h-12">
            {loadingProvider === "google" ? (
              <Loader2 className="mr-3 h-5 w-5 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="mr-3"
                viewBox="0 0 16 16"
              >
                <path d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z" />
              </svg>
            )}
            {isLoading && loadingProvider === "google" ? t("loggingIn") : t("googleLogin")}
          </Button>

          {/* GitHub登录按钮 */}
          <Button onClick={handleGitHubLogin} disabled={isLoading} variant="outline" className="w-full h-12">
            {loadingProvider === "github" ? (
              <Loader2 className="mr-3 h-5 w-5 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="mr-3"
                viewBox="0 0 16 16"
              >
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
              </svg>
            )}
            {isLoading && loadingProvider === "github" ? t("loggingIn") : t("githubLogin")}
          </Button>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm text-muted-foreground">
            {t("loginTerms")}{" "}
            <Link href="/terms" className="text-primary hover:underline">
              {t("termsOfService")}
            </Link>{" "}
            {t("and")}{" "}
            <Link href="/privacy" className="text-primary hover:underline">
              {t("privacyPolicy")}
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
